import { Col, Row } from 'react-bootstrap';
import { observer } from 'mobx-react';
import { Notifications } from '../../../../Notifications';
import i18n from 'i18next';
import { GetItemQuery, InvoiceItem } from '../../../../../core/models';
import { useStores } from '../../../../../hooks';
import { toJS } from 'mobx';
import { SearchBar } from '../../../../../components/mui/searchBar';

enum SeatchType {
    CLASSIFICATION = 'classification',
    DESCRIPTION = 'description',
}

const AUTO_COMPLETE_SEARCH_SIZE = 20;
const CLASSIFICATION_TRIGGERS = ['class: ', 'class:'];

interface PageProps {
    searchItems: (query: GetItemQuery) => Promise<void>;
    searchItemsForAutoComplete?: (query: GetItemQuery) => Promise<InvoiceItem[]>;
}

export const ItemSearchPanel = observer(function ItemSearchPanel(props: PageProps) {
    const {
        domain: { itemsPageStore, constantsStore },
    } = useStores();

    const { searchItems, searchItemsForAutoComplete } = props;
    const currentFilter = toJS(itemsPageStore.filter) || { searchValue: '' };
    const { searchValue, searchType, classId } = currentFilter;

    const isValidSearch = (value) => {
        const valueLength = value.trim().length;
        const validationError =
            valueLength === 0 &&
            i18n.t('validation:searchNotEmpty', {
                field: i18n.t('controlCenter:itemSearch.id', 'ID'),
            });

        if (validationError) {
            Notifications.warn(validationError);
            return false;
        }
        return true;
    };

    const search = () => {
        const searchQuery = { searchType: searchType, searchValue: searchValue };

        const trigger = CLASSIFICATION_TRIGGERS.find((trigger) => {
            if (searchValue?.toLowerCase().includes(trigger)) {
                return trigger;
            }
            return undefined;
        });

        if (trigger && searchValue?.toLowerCase().startsWith(trigger)) {
            searchQuery.searchValue = searchValue.substring(trigger?.length);
            searchQuery.searchType = 'classification';
        }

        if (!classId && !isValidSearch(searchQuery.searchValue)) {
            return;
        }

        searchItems(searchQuery);
    };

    const updateFilter = (update) => {
        itemsPageStore.filter = update;
    };

    const requestAutoCompleteValues = async (value: string): Promise<string[]> => {
        const classPrefix = CLASSIFICATION_TRIGGERS.find((trigger) => {
            if (value?.toLowerCase().includes(trigger)) {
                return trigger;
            }
            return undefined;
        });

        const query: GetItemQuery = { searchValue: value, size: AUTO_COMPLETE_SEARCH_SIZE, unique: true };
        const classificationQuery: GetItemQuery = {
            searchValue: classPrefix ? `*${value.substring(classPrefix.length)}*` : value,
            searchType: SeatchType.CLASSIFICATION,
            size: AUTO_COMPLETE_SEARCH_SIZE,
            unique: true,
        };

        const items: InvoiceItem[] | undefined = !classPrefix ? await searchItemsForAutoComplete?.(query) : [];
        const classItems = await searchItemsForAutoComplete?.(classificationQuery);

        const filtered =
            items
                ?.filter((value, index, self) => {
                    return index === self.findIndex((t) => t.hospitalDescription === value.hospitalDescription);
                })
                .map((item) => {
                    return item.hospitalDescription;
                }) || [];

        const classFiltered =
            classItems
                ?.filter((value, index, self) => {
                    return index === self.findIndex((t) => t.classification.name === value.classification.name);
                })
                .map((item) => {
                    return `Class: ${item.classification.name}`;
                }) || [];

        return [...filtered, ...classFiltered].sort((a, b) => a.localeCompare(b));
    };

    return (
        <>
            <Row>
                <Col xs={12}>
                    <SearchBar
                        disabled={constantsStore.isLoading}
                        isSelfControlled={false}
                        value={currentFilter.searchValue} //hard coded 'description' or 'id' to filter new SearchBar search results with description or id
                        onChange={(searchValue) => updateFilter({ searchValue })}
                        // onClick={this.showPresets} //keep this prop for future functionality
                        onSubmit={search}
                        searchItemsForAutoComplete={requestAutoCompleteValues}
                    />
                </Col>
            </Row>
        </>
    );
});
